import GameObject, { GameObjectEvent } from 'GameObject/GameObject';
import FarmMap from 'Prototype/Farm/FarmMap';
import BaseUIHandler from 'System/UI/BaseUIHandler';
import { Layout } from 'Utility/layout/createLayout';
import { PopupID } from 'State/UI/PopupID';
import BaseUISystem from 'System/UI/BaseUISystem';
import RenderSystem, { RenderSystemEvent } from 'System/Render/RenderSystem';
import Point from 'Utility/Point';
import RelativePositionComponent from 'Component/Render/RelativePosition';
import { FarmSlot } from 'Prototype/Farm/FarmSlot';
import { IGameState } from 'State/Game/IGameState';
import Game from 'Game';
import Interactable from 'Component/Interactable';
import { UIActions } from 'State/UI/UIActions';
import { FarmAreaId, FarmSlotId, IFarmPlayerState } from 'State/Player/Farm/IFarmPlayerState';
import Renderable from 'Component/Renderable';
import { PetId } from 'Constants/Pets';
import Pet from 'Prefabs/Pet/Pet';
import { PetAnimations } from 'Utility/Helpers/Pets/PetAnimations';
import { PetArmature } from 'Utility/Helpers/Pets/PetArmature';
import FarmMiniGameTimer from 'System/Timer/Timers/FarmMiniGameTimer';
import DateHelper from 'Utility/DateHelper';
import TextComponent from 'Component/Render/Text';
import DragonbonesComponent from 'Component/Dragonbones';
import Text from 'Prototype/Text';
import TransformComponent from 'Component/Transform';
import { MathHelper } from 'Utility/MathHelper';
import { PlaySound } from 'System/Audio/AudioHelper';
import { FarmEnvironmentEffect } from '../FarmEffect';
import { FarmEffectData } from '../FarmEffectData';
import { FARM_ENVIRONMENT_EFFECTS } from 'Constants/Farm';
import { StartAnimationClip } from 'System/Animation/AnimationHelper';
import { Alpha } from 'System/Animation/Clip';
import { canPurchaseArea } from '../FarmHelpers';
import { FarmStateHelper } from 'State/Player/Farm/FarmStateHelper';
import { FTUXStateHelper } from 'State/Game/FTUX/FTUXStateHelper';
import { FTUXType } from 'Player/PlayerProperty/Progress/FTUXType';

type FarmMapProps = {
    system: BaseUISystem;
};

export default class FarmMapHandler extends BaseUIHandler {
    private map: FarmMap;
    private layout: any;
    private static readonly areas: GameObject[] = [];
    public readonly borderAreas: GameObject[] = [];
    private timer: FarmMiniGameTimer;
    private timerText: Text;
    public pet: Pet;
    private static readonly slots: FarmSlot[] = [];
    private static sortedSlots: FarmSlot[] = [];

    constructor(props: FarmMapProps) {
        super(PopupID.FARM_MAP, props.system, false);
        this.alwaysVisible = true;

        RenderSystem.on(RenderSystemEvent.RESIZE, this.handleResize);
        this.cleanupManager.add(() => {
            RenderSystem.off(RenderSystemEvent.RESIZE);
        });
    }

    public getLayout(): Layout {
        return this.layout;
    }

    private readonly handleResize = (): void => {
        this.map?.handleResize();
    };

    public onCreate(): void {
        super.onCreate();

        FarmMapHandler.instance = this;
        this.cleanupManager.add(() => {
            FarmMapHandler.instance = null;
        });

        this.map = GameObject.Instantiate(FarmMap, { pivot: Point.Center });
        this.system.scene.addGameObject(this.map);
        this.map.addComponent(RelativePositionComponent, { x: 0.5, y: 0.5 });

        this.layout = this.map.getLayout();

        const state = Game.store.getState();
        const farmState = state.playerState.farmState;
        // Create all required slots
        this.handleSlots(farmState);
        if (state.playerState.petsState.activePet) {
            this.setupPet(state);
        }
        FARM_ENVIRONMENT_EFFECTS.forEach(effectData => this.addEnvironmentEffect(effectData));

        // const unseenPremiumItems = FarmStateHelper.getUnseenPremiumItems(state.playerState.decoItemsSeen);
        // if (unseenPremiumItems.length > 0 && FTUXStateHelper.isFtuxCompleted(state.playerState, FTUXType.FARM)) {
        //     UIActions.showPopup(PopupID.FARM_NEW_ITEMS, {});
        // }
    }

    private readonly onUnlockAreaClicked = (areaID: FarmAreaId) => {
        UIActions.showPopup(PopupID.FARM_AREA_UNLOCK, areaID);
    };

    private readonly handleSlots = (farmState: IFarmPlayerState) => {
        this.layout.areaContainer.children.forEach((areaObject: any, index: number) => {
            const area = this.layout.areaContainer.area[index];
            const areaId = areaObject.container.name;
            const borderArea = area.border_area;

            borderArea.getComponent(Interactable).enabled = false;
            borderArea.getComponent(Interactable).buttonMode = false;
            borderArea.getComponent(Renderable).visible = false;
            borderArea.on(GameObjectEvent.SELECTED, () => {
                this.onUnlockAreaClicked(areaId);
            });
            FarmMapHandler.areas.push(areaObject);

            if (farmState.unlockedAreas.includes(areaId)) {
                this.initFarmArea(areaId);
            }
        });

        this.sortFarmSlots();
    };

    private setupPet(state: IGameState): void {
        this.timer = new FarmMiniGameTimer();
        this.timer.onUpdate = this.updateText;
        this.timer.onComplete = this.handlePetAwake;

        const timerDate = this.timer.getDate(state);

        // Create current active pet
        this.pet = GameObject.Instantiate(Pet, {
            petId: PetId.Charly,
            x: 0,
            y: -35,
            pivot: new Point(0, 0),
            scale: new Point(0.35, 0.35),
            armatureBlacklist: [PetArmature.MINI_GAME_FED, PetArmature.MINI_GAME_HUNGRY],
        });
        this.pet.on(GameObjectEvent.SELECTED, () => this.handlePetClick());
        this.pet.initialized.then(() => {
            this.pet.play(timerDate === null ? PetAnimations.HUNGRY : PetAnimations.SLEEPING);
            this.timerText = GameObject.Instantiate(Text, {
                text: '24:00:00',
                style: {
                    fontSize: 24,
                    fontFamily: 'FrancoisOne',
                    fill: '#ba372b',
                },
                // anchor: new Point(0.5,0.5),
            });

            this.updateText(state);

            const petArmature = this.pet.armatures.get(PetArmature.MY_FARM);
            const petDragonBonesComponent = petArmature.getComponent(DragonbonesComponent);
            petDragonBonesComponent.getSlotDisplay('pet_text_cloud').addChild(this.timerText.container);

            const timerTextTransform = this.timerText.getComponent(TransformComponent);
            timerTextTransform.position.set(25, 50);
            timerTextTransform.scale.set(1.6);
        });
        this.layout.petContainer.addChild(this.pet);

        this.pet.getComponent(Interactable).update({ buttonMode: timerDate === null, enabled: timerDate === null });

        this.system.scene.on('PetSleeping', this.handlePetSleep);
    }

    private addEnvironmentEffect(data: FarmEffectData): FarmEnvironmentEffect {
        const effect = GameObject.Instantiate(FarmEnvironmentEffect, data);
        this.layout.effectsContainer.addChild(effect);
        return effect;
    }

    public readonly updateText = (state: IGameState = Game.store.getState()): void => {
        if (this.layout.root.destroyed) return;

        const time: Date = this.timer.getDate(state);

        if (time === null) return;

        const textComponent = this.timerText.getComponent(TextComponent);
        const { duration, format } = this.timer.getDuration();

        const text: string = DateHelper.timeTo(DateHelper.now, DateHelper.getDate(time.getTime() + duration), format);

        textComponent.text = text;
    };

    public readonly handlePetAwake = (): void => {
        const timerDate = this.timer.getDate(Game.store.getState());
        this.pet.getComponent(Interactable).update({ buttonMode: timerDate === null, enabled: timerDate === null });
        this.pet.stop();
        this.pet.playOnce(PetAnimations.AWAKENING).then(() => {
            this.pet.play(PetAnimations.HUNGRY);
        });
    };

    public readonly handlePetSleep = (): void => {
        const timerDate = this.timer.getDate(Game.store.getState());
        this.pet.getComponent(Interactable).update({ buttonMode: timerDate === null, enabled: timerDate === null });
        this.pet.stop();
        const petsState = Game.store.getState().playerState.petsState;
        PlaySound(`${petsState.activePet}_fallingasleep`);
        this.pet.playOnce(PetAnimations.FALLING_ASLEEP).then(() => {
            this.pet.play(PetAnimations.SLEEPING);
        });
    };

    private readonly handlePetClick = async () => {
        // Stop pet animation on map cause there is a pet animated in the mini game popup
        this.pet.stop();
        UIActions.showPopup(PopupID.FARM_MINI_GAME, {});
    };

    public handleStateChanged(state: IGameState): void {
        const prevState = Game.store.getPreviousState();
        super.handleStateChanged(state);
        FarmMapHandler.slots.forEach(slot => slot.handleStateChanged(state));

        const farmState = state.playerState.farmState;
        this.layout.areaContainer.children.forEach((areaObject: any, index: number) => {
            const area = this.layout.areaContainer.area[index];
            const areaId = areaObject.container.name;

            const hideIAPBorderArea = !canPurchaseArea(areaId);

            const borderArea = area.border_area;
            const enabled =
                !farmState.unlockedAreas.includes(areaId) && !hideIAPBorderArea
                    ? state.farmState.constructionModeEnabled
                    : false;

            borderArea.getComponent(Interactable).update({
                enabled,
                buttonMode: enabled,
            });
            borderArea.getComponent(Renderable).visible = enabled;

            const newUnlockedAreas = farmState.unlockedAreas.filter(
                areaId => !prevState.playerState.farmState.unlockedAreas.includes(areaId)
            );
            if (newUnlockedAreas.length > 0) {
                PlaySound('purchase_new_area');
                newUnlockedAreas.forEach(areaId => {
                    this.initFarmArea(areaId, true);
                });
                this.sortFarmSlots();
            }
        });

        // TODO: Prevent map from scrolling when ui menu is open
        // TODO: Do not use "previous state".
        if (state.farmState.constructionModeEnabled !== prevState.farmState.constructionModeEnabled) {
            StartAnimationClip(
                new Alpha(this.layout.effectsContainer, {
                    startAlpha: Number(state.farmState.constructionModeEnabled),
                    endAlpha: Number(!state.farmState.constructionModeEnabled),
                    duration: 350,
                })
            );
        }
    }

    private initFarmArea(areaId: FarmAreaId, animate: boolean = false): void {
        const areaLayout = FarmMapHandler.getAreaLayout(areaId);
        areaLayout.slotsContainer.getComponent(Renderable).visible = true;
        areaLayout.slots.children.map((slot: any, index: number) => {
            const farmSlot = GameObject.Instantiate(FarmSlot, {
                slotId: slot.container.name,
                areaId,
                selectNextSlot: (_direction: 'left' | 'right') => {
                    const sortedSlots = FarmMapHandler.sortedSlots;
                    const currentIndex = sortedSlots.indexOf(farmSlot);
                    sortedSlots[
                        MathHelper.repeat(
                            _direction === 'left' ? currentIndex - 1 : currentIndex + 1,
                            sortedSlots.length
                        )
                    ]?.onSlotClicked();
                },
                getScreenPos: (): Point => {
                    return new Point(this.map.container.position.x, this.map.container.position.y);
                },
                mapLayout: this.getLayout(),
            });
            slot.addChild(farmSlot);
            FarmMapHandler.slots.push(farmSlot);
            farmSlot.handleIcons(this.layout.farmUI);

            // Sync slot to state before animating. This sets the correct initial visibility of the icons.
            farmSlot.handleStateChanged(Game.store.getState());

            if (animate) {
                farmSlot.animatePurchase(index);
            }
        });
    }

    private sortFarmSlots(): void {
        FarmMapHandler.sortedSlots = FarmMapHandler.slots.slice().sort((a, b) => {
            return (
                a.parent.getComponent(TransformComponent).position.x -
                b.parent.getComponent(TransformComponent).position.x
            );
        });
    }

    public static getArea(areaId: FarmAreaId): GameObject {
        return this.areas.find(area => area.container.name === areaId);
    }

    public static getAreaLayout(areaId: FarmAreaId) {
        const index = this.areas.findIndex(area => area.container.name === areaId);
        return this.instance?.layout.areaContainer.area[index];
    }

    public onDestroy() {
        super.onDestroy();

        FarmMapHandler.slots.length = 0;
    }

    public static getSlot(slotId: FarmSlotId): FarmSlot | undefined {
        return this.slots.find(slot => slot.slotID === slotId);
    }
    private static instance: FarmMapHandler = null;
}
